/**
 * 图片网格组件
 * 展示图片列表
 */

import React, { useState } from 'react';
import { ZoomIn, ImageIcon } from 'lucide-react';

export interface DisplayImage {
  src: string;
  title: string;
  isSaved: boolean;
  originalIndex: number;
  /** I7+8: 图片相关性评分（0-10），由视觉模型打分，高相关=绿色，低相关=红色 */
  score?: number;
  /** I8: 是否为低相关性图片（AI 判定 1-3 分），展示时降低透明度并加虚线边框 */
  lowRelevance?: boolean;
}

interface ImageGridProps {
  /** 要显示的图片列表 */
  images: DisplayImage[];
  /** 图片错误处理函数 */
  onImageError: (index: number, originalIndex: number) => void;
  /** 点击图片回调 */
  onImageClick: (src: string, title: string) => void;
}

/**
 * 图片网格组件
 * 以网格形式展示图片，支持点击查看大图
 */
export const ImageGrid: React.FC<ImageGridProps> = ({
  images,
  onImageError,
  onImageClick
}) => {
  // P3: 使用 Map（key 为 originalIndex）追踪失败状态，而非 Set（key 为本地索引）
  // 这样即使 results 重新筛选导致本地索引变化，失败状态仍可准确对应到原始图片
  const [failedMap, setFailedMap] = useState<Map<number, boolean>>(new Map());

  const handleImgError = (index: number, originalIndex: number) => {
    setFailedMap(prev => {
      const next = new Map(prev);
      next.set(originalIndex, true);
      return next;
    });
    onImageError(index, originalIndex);
  };

  if (images.length === 0) return null;

  // I7+8: 评分显示颜色映射
  const getScoreColor = (score?: number) => {
    if (score === undefined) return '';
    if (score >= 7) return 'bg-emerald-500/80';
    if (score >= 4) return 'bg-amber-500/80';
    return 'bg-red-500/80';
  };

  return (
    <div className="grid grid-cols-3 gap-1.5">
      {images.map((img, i) => {
        // 优先使用 originalIndex 作为 key 判断是否已失败，兼容 originalIndex = -1 的情况
        const hasFailed = img.originalIndex >= 0 ? failedMap.has(img.originalIndex) : false;
        const isValidSrc = img.src && (img.src.startsWith('http') || img.src.startsWith('/') || img.src.startsWith('data:'));
        // I8: 低相关图片：opacity-60 + 虚线边框
        const isLowRelevance = img.lowRelevance && !img.isSaved;

        return (
          <div
            key={`img-${i}`}
            className={[
              'relative group/img aspect-square rounded-md overflow-hidden cursor-pointer transition-all',
              isValidSrc && !hasFailed ? 'hover:border-blue-400/40' : '',
              isLowRelevance
                ? 'opacity-60 border-dashed border-amber-500/30'
                : 'bg-white/[0.03] border border-white/5',
            ].join(' ')}
            onClick={() => !hasFailed && isValidSrc && onImageClick(img.src, img.title)}
          >
            {isValidSrc && !hasFailed ? (
              <img
                src={img.src}
                alt={img.title}
                className={[
                  'w-full h-full object-cover transition-transform',
                  isLowRelevance ? 'group-hover/img:scale-105' : 'group-hover/img:scale-105',
                ].join(' ')}
                loading="lazy"
                referrerPolicy="no-referrer"
                crossOrigin="anonymous"
                onError={() => handleImgError(i, img.originalIndex)}
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-1">
                <ImageIcon size={16} className="text-gray-600" />
                <span className="text-[9px] text-gray-600 text-center px-1 line-clamp-2">{img.title}</span>
              </div>
            )}

            {/* 悬停遮罩：缩放 + 评分气泡（I7+8） */}
            {isValidSrc && !hasFailed && (
              <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/20 transition-colors flex items-center justify-center gap-1">
                <ZoomIn size={16} className="text-white opacity-0 group-hover/img:opacity-80 transition-opacity" />
                {/* I7: 评分气泡：score 存在时显示 */}
                {img.score !== undefined && (
                  <div className={[
                    'absolute top-1 right-1 px-1 py-0.5 rounded text-[9px] text-white font-medium',
                    'opacity-0 group-hover/img:opacity-100 transition-opacity',
                    getScoreColor(img.score),
                  ].join(' ')}>
                    {img.score}
                  </div>
                )}
                {/* I8: 低相关标记：虚线角标 */}
                {isLowRelevance && (
                  <div className="absolute bottom-1 left-1 px-1 py-0.5 rounded bg-amber-500/80 text-[8px] text-white">
                    低相关
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
